import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Languages, Loader2 } from 'lucide-react';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Triggers POST /api/translate/bulk/<scope> for one language.
 *
 * Props:
 *   scope: 'curated-destinations' | 'partner-packages' | 'about'
 *   targetLang: 'es' | 'fr' | 'pt'  (default 'es')
 *   targetLabel: 'Spanish' | ...    (default 'Spanish')
 *   onComplete: () => void          optional refetch hook
 */
const BulkTranslateButton = ({
  scope,
  targetLang = 'es',
  targetLabel = 'Spanish',
  onComplete,
}) => {
  const [busy, setBusy] = useState(false);

  const run = async (overwrite = false) => {
    if (overwrite && !window.confirm(`Re-translate ALL existing ${targetLabel} content? This overwrites the current ${targetLabel} version of every record.`)) {
      return;
    }
    setBusy(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/translate/bulk/${scope}`,
        { target_lang: targetLang, overwrite },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 180000 }
      );
      const { translated, skipped, failed, details = [] } = res.data || {};
      const parts = [];
      if (translated) parts.push(`${translated} translated`);
      if (skipped) parts.push(`${skipped} skipped`);
      if (failed) parts.push(`${failed} failed`);
      const summary = parts.length ? parts.join(' · ') : 'Nothing to do';

      if (failed > 0) {
        toast.error(`Bulk translate (${targetLabel}): ${summary}`);
      } else if (translated > 0) {
        toast.success(`Bulk translate (${targetLabel}): ${summary}`);
      } else {
        toast.info(`Bulk translate (${targetLabel}): ${summary}`);
      }
      if (details.length) console.log(`[bulk-translate ${scope}/${targetLang}]`, details);
      if (onComplete) onComplete();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Bulk translate failed';
      toast.error(`Bulk translate failed: ${msg}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="inline-flex items-center gap-2">
      <Button
        type="button"
        onClick={() => run(false)}
        disabled={busy}
        variant="outline"
        className="border-terracotta text-terracotta hover:bg-terracotta/10 inline-flex items-center gap-2"
        data-testid={`bulk-translate-${scope}-btn`}
        title={`Translate every record that's missing a ${targetLabel} version`}
      >
        {busy ? <Loader2 size={16} className="animate-spin" /> : <Languages size={16} />}
        {busy ? `Translating…` : `Translate all to ${targetLabel}`}
      </Button>
      <button
        type="button"
        onClick={() => run(true)}
        disabled={busy}
        className="text-xs text-maroon/50 hover:text-maroon underline"
        data-testid={`bulk-translate-${scope}-overwrite-btn`}
        title={`Re-translate every record, overwriting existing ${targetLabel} content`}
      >
        re-translate all
      </button>
    </div>
  );
};

export default BulkTranslateButton;
